/**
 * Catalogue TAXONOMY API — the category tree and the attribute dictionary.
 *
 * CONTRACT SOURCE: the API's M12 catalogue-taxonomy contracts and operation
 * register. The paths below are relative to the transport's base URL (`/api/v1`).
 *
 * ---------------------------------------------------------------------------
 * THE IDEMPOTENCY KEY IS THE CALLER'S
 * ---------------------------------------------------------------------------
 * Every write here carries an `Idempotency-Key`, and the key is passed IN rather
 * than generated inside this service. The key identifies ONE item of ONE save,
 * and it is reused verbatim when that item is retried — a fresh key on a retry
 * is how one merchant action becomes two records. The caller owns the key's
 * lifetime; this service only sends it.
 *
 * ---------------------------------------------------------------------------
 * THE ATTRIBUTE SET IS A FULL-SET REPLACE
 * ---------------------------------------------------------------------------
 * `setCategoryAttributes` REPLACES a category's attribute set: it is not an
 * append. It is also the one write here that is not idempotency-keyed — it is
 * convergent by construction, and the API arbitrates concurrency with the
 * category's DRAFT cas token instead. That token travels in `If-Match` as a
 * BARE integer (M12 refuses the quoted entity-tag form), and the API returns
 * the next draft version, which is what the next write to that category must
 * carry.
 */
import { http, request } from '@/api/client/http';
import {
  AttributeDefinitionListResponseSchema,
  AttributeDefinitionResponseSchema,
  CategoryAttributeSetResponseSchema,
  CategoryListResponseSchema,
  CategoryResponseSchema,
} from '@/api/schemas/catalog-taxonomy';
import type {
  AttributeDefinition,
  Category,
  CategoryAttributeInput,
  CategoryAttributeSet,
  CreateAttributeDefinitionRequest,
  CreateCategoryRequest,
} from '@/api/schemas/catalog-taxonomy';

export const taxonomyApi = {
  /** GET /catalog/categories — the tenant's own category tree. */
  async listCategories(): Promise<Category[]> {
    const response = await http.get('/catalog/categories', CategoryListResponseSchema);
    return response.data;
  },

  /** GET /catalog/attribute-dictionary — the tenant's attribute definitions. */
  async listAttributeDictionary(): Promise<AttributeDefinition[]> {
    const response = await http.get(
      '/catalog/attribute-dictionary',
      AttributeDefinitionListResponseSchema,
    );
    return response.data;
  },

  /**
   * POST /catalog/categories — creates one category.
   *
   * `idempotencyKey` identifies this category in this save. The same key on a
   * retry is a retry; a different key is a second category.
   */
  async createCategory(
    input: CreateCategoryRequest,
    idempotencyKey: string,
  ): Promise<Category> {
    const response = await http.post('/catalog/categories', CategoryResponseSchema, {
      body: input,
      idempotencyKey,
    });
    return response.data;
  },

  /**
   * POST /catalog/attribute-dictionary — creates one attribute definition.
   */
  async createAttributeDefinition(
    input: CreateAttributeDefinitionRequest,
    idempotencyKey: string,
  ): Promise<AttributeDefinition> {
    const response = await http.post(
      '/catalog/attribute-dictionary',
      AttributeDefinitionResponseSchema,
      { body: input, idempotencyKey },
    );
    return response.data;
  },

  /**
   * PUT /catalog/categories/{categoryId}/attributes — replaces the category's
   * attribute set.
   *
   * `draftVersion` is the category's draft cas token as this console last
   * learned it. The API compares it with the category's own token and refuses a
   * stale one with a 409 rather than overwriting somebody else's edit.
   *
   * The call is a PUT, and the API answers PATCH on this path with a 404: the
   * route is a full-set replace, which is why the caller must send the WHOLE
   * set it intends the category to have.
   */
  async setCategoryAttributes(
    categoryId: string,
    attributes: ReadonlyArray<CategoryAttributeInput>,
    draftVersion: number,
  ): Promise<CategoryAttributeSet> {
    const response = await request(
      `/catalog/categories/${categoryId}/attributes`,
      CategoryAttributeSetResponseSchema,
      {
        method: 'PUT',
        body: { attributes },
        ifMatch: draftVersion,
        ifMatchFormat: 'bare-version',
      },
    );
    return response.data;
  },
};
